/**
 * iFederated's PRODUCT IDENTITY: the name, the apex it answers on, and the few sentences its shell says. iFederated
 * (`apps/ifederated`, operator decision 2026-09-18) is the ONE place a person signs in -- the single sign-on hub every
 * app sends a signed-out visitor to -- and the direct path to that person's own profile.
 *
 * WHY ITS OWN APEX, NOT THE BRAND'S. The active brand (`federa.com`) is one app among several, and the hub serves all of
 * them, `kenecting.com` included: a login that lived under one app's domain would make every other product a guest of
 * it (operator, 2026-09-18: "federa is just 1 app, it shouldn't be the default federated login"). The GRAMMAR is still
 * the fleet's, read from the one composer (`apexHostnames` / `httpsOrigins`, `const/brand-hostnames.ts`) over this
 * apex, the same tier `const/kenecting-identity.ts` established for a product that answers on its own domain.
 */

import { apexHostnames, httpsOrigins } from './brand-hostnames.ts';
import { BINDINGS_SECRETS } from './bindings-secrets.ts';
import { STRIPE_SECRET_KEY_BINDING } from './payments.ts';
import type { WranglerEnvKey } from './config-environments.ts';

export const IFEDERATED_PRODUCT_NAME = 'iFederated';

/** The hub's own apex -- a zone on the fleet's Cloudflare account, which the app's manifest attaches as each env's
 *  custom domain at deploy. */
export const IFEDERATED_APEX = 'ifederated.com';

/** The HOSTNAME the hub answers on, per wrangler env block: the apex in production, `dev.` under it for dev. */
export const IFEDERATED_APP_HOSTNAMES: Readonly<Record<WranglerEnvKey, string>> = apexHostnames(IFEDERATED_APEX, null);

/** The public ORIGIN the hub answers on, per wrangler env block -- what its manifest binds as `NEXT_PUBLIC_APP_URL` and
 *  where every other app sends a visitor to sign in. */
export const IFEDERATED_APP_URLS: Readonly<Record<WranglerEnvKey, string>> = httpsOrigins(IFEDERATED_APP_HOSTNAMES);

/**
 * THE HUB'S OWN AUTH API MOUNT (operator decision 2026-09-21: everything login-related lives in this one app, frontend
 * and backend, and no auth worker exists). The login flows run IN-PROCESS behind this base, the way `apps/noor` runs
 * its partners flows behind `NOOR_PARTNERS_API_BASE`: the hub's route handler at `app/api/auth/[flow]` hands the
 * request to the fleet's dispatch, and the path after the prefix IS the flow id. The hub's own `AuthProvider` dials
 * it same-origin through `AuthProviderConfig.authServiceUrl`.
 *
 * OUTSIDE THE SHELL MIDDLEWARE'S MATCHER on purpose: `/__auth/*` is the prefix the shared forwarder hands to a
 * service binding, and the hub binds none -- a route under `/api/` reaches the Next router untouched.
 */
export const IFEDERATED_AUTH_API_BASE = '/api/auth';

/** The prefix the hub's dispatch strips to read the flow id; trailing slash included, as the factory expects. */
export const IFEDERATED_AUTH_API_PREFIX = `${IFEDERATED_AUTH_API_BASE}/`;

/**
 * The PATH one login flow answers on at the hub -- what a browser client composes on the hub's origin
 * (`AUTH_SERVICE_ROUTES`, `const/auth-routes.ts`) and what a server caller hands the flow transport over the hub's
 * service binding (`apps/federa`'s session mint). The last segment IS the flow id, by the same prefix rule the dispatch
 * reads it with.
 * @runtime-derived-ssot-helper composes {@link IFEDERATED_AUTH_API_PREFIX}; no second spelling of the mount.
 */
export function ifederatedAuthFlowPath(flowId: string): string {
	return `${IFEDERATED_AUTH_API_PREFIX}${flowId}`;
}

/** The hub's generated wrangler config, repo-relative -- the key `WRANGLER_SECRETS_REQUIRED_SSOT`
 *  (`const/dev-stack-required-env.ts`) pairs the allowlist below with. Spelled here rather than there because the path
 *  is a fact about THIS service, mirroring `TELEMETRY_RELAY.WRANGLER_CONFIG`. */
export const IFEDERATED_WRANGLER_CONFIG = 'apps/ifederated/wrangler.jsonc' as const;

/**
 * The hub's PLAIN worker secrets (`wrangler secret put --env <env>` in `apps/ifederated`), read by the manifest's
 * `secrets.required` block and by `INFRA-FLOWRUNNER-SECRETS-REQUIRED-PARITY-001`. Since 2026-09-21 every login flow
 * runs in-process here, so this is the ONE deployment that holds them.
 *
 * WHY NEITHER IS A SECRETS STORE ENTRY. An OpenNext deploy credential cannot read the Secrets Store
 * (`SEC-APPS-NO-SECRETS-STORE-BINDING-001` names `wrangler secret put` as the app-tier route), and the Firebase
 * service-account key file EXCEEDS the store's per-value size limit anyway (operator, 2026-09-19, after the first
 * dev deploy): the key file carries a PEM private key, so the store could not hold it at all. This is a capacity fact
 * about the value, not a posture choice -- the key is no less secret for being supplied this way, and `wrangler
 * secret put` is the same encrypted-at-rest channel the telemetry relay's two credentials take.
 *
 * NEVER ALSO A STORE BINDING: the two mechanisms are mutually exclusive per secret, and a name on both sides makes
 * wrangler refuse the config outright so every wrangler command exits 1 (`types/wrangler-service-manifest.ts`;
 * matcher `INFRA-WRANGLER-SECRETS-REQUIRED-STORE-CONFLATION-001`).
 *
 * THE DEPLOY CONSEQUENCE IS INTENDED: wrangler turns a declared name into an `inherit` binding at upload, so a deploy
 * of the hub REFUSES while either is unprovisioned for that env. Without the service account every `sso-*` flow
 * answers `temporarily_unavailable` and nobody can sign in; without the Stripe key `user-provision`'s Stripe-user
 * leaf cannot run. Failing the deploy is the correct answer.
 */
export const IFEDERATED_PLAIN_SECRETS: readonly string[] = [BINDINGS_SECRETS.FIREBASE_SERVICE_ACCOUNT, STRIPE_SECRET_KEY_BINDING];

/** Every sentence the hub's own shell says; the profile it hosts keeps its own words. */
export const IFEDERATED_APP_COPY = {
	/** What the hub is for, one line, so it sits under the sign-in modal's title without wrapping. */
	purpose: 'One sign-in for every Federated app.',
	/** Shown while the hub hands a signed-in visitor back to the app that sent them. */
	returning: 'Taking you back...',
	/** Shown while the sign-out page ends the session -- the act, true whether an app waits at the end or the hub's own
	 *  sign-in box does. */
	signingOut: 'Signing you out...',
	/** A return address the hub does not recognise: it names no app, so it sends nobody anywhere. */
	unknownApp: 'This sign-in link is not from a Federated app.',
} as const;
